import { FC, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import cs from 'classnames';
import { useMethods } from '@lhb/hook';
import { isArray, refactorSelection } from '@lhb/func';
import { useTenantCheckInfo } from '@/common/hook/useTenantCheckInfo';
import { franchiseePage, getFranchiseeDetail, getFranchiseeSearch, getFranchiseeTemplateList } from '@/common/api/expandStore/franchisee';
import styles from './index.module.less';
import { Form } from 'antd';
import V2Fuzzy from '@/common/components/Form/V2Fuzzy';
import V2Empty from '@/common/components/Data/V2Empty';
import IconFont from '@/common/components/IconFont';
import FranchiseeSelectTemplate from '@/common/components/business/ExpandStore/FranchiseeSelectTemplate';
import FranchiseeCreateDrawer from '@/common/components/business/ExpandStore/FranchiseeCreateDrawer';

/**
 * @Description 拓店标准版 form中的加盟商列表（模糊查询）
 */
const FormFranchisee: FC<{
  /** 表单name */
  name: string;
  /** 表单label */
  label: string;
  /** 接口入参 */
  extraParams?: any;
  /** 表单item配置 */
  formItemConfig?: any;
  /** 创建按钮文案 */
  createBtnTitle?: string;
  /** 创建成功回调 */
  onCreated?: Function;
  [key: string]: any;
}> = forwardRef(({
  name,
  label,
  extraParams = { // 接口入参
    page: 1,
    size: 50,
  },
  formItemConfig,
  createBtnTitle = '去创建加盟商',
  onCreated,
  ...props
}, ref) => {

  const {
    // /** 当前租户的拓店任务模板是否“加盟+直营A（基诺浦）”，未确定时为null */
    // isStandardAndClusterATaskTemplate,
    /** 当前租户的拓店任务模板是否“加盟+直营B（一鸣）”，未确定时为null */
    isStandardAndClusterBTaskTemplate,
  } = useTenantCheckInfo();

  useImperativeHandle(ref, () => ({
    // 用来对外抛出完整的原始数据，当外部需要的不仅是id，而是更多item内的数据时使用
    // 场景示例：外部包了一层FormCom 组件，在onCHange时，通过getData拿到数据，然后包装好后，可以传出 {id: 1, name: xx, mobile: yy, ...} 等完整item数据。
    // getData: () => data,
    // 用来插入额外的option
    // 场景示例：点位弹窗，点击新增点位后，为了不让fuzzy重新loadData数据，可以直接 add一条新添加的数据进去即可。下次查询操作后就会被重置。
    addOption: fuzzyRef.current?.addOption,
    // 用来插入默认的options，此时需要设置props.immediateOnce 为 false
    // 场景示例：在编辑弹窗反向填充
    setOptions: fuzzyRef.current?.setOptions,
    // selectRef: fuzzyRef.current?.selectRef,
    getItem: (data) => fuzzyRef.current?.getItem(data)
  }));

  const fuzzyRef: any = useRef(null);
  const [selectTemplateData, setSelectTemplateData] = useState<any>({
    open: false,
    options: [],
  });
  const [formDrawerData, setFormDrawerData] = useState<any>({
    open: false,
    templateId: '', // 模板id
    id: '', // 编辑时的id
  });

  const methods = useMethods({

    loadData: async (keyword?: string) => {
      const params = {
        keyword,
        ...extraParams
      };

      let list: any[] = [];
      if (isStandardAndClusterBTaskTemplate) {
        const res = await getFranchiseeSearch(params);
        list = isArray(res) ? res : [];
      } else {
        const res = await franchiseePage(params);
        const { objectList, meta } = res || {};
        const { uniqueName } = meta || {};
        list = isArray(objectList) ? objectList.map((itm: any) => ({
          ...itm,
          uniqueName
        })) : [];
      }

      list = list.map(item => ({ ...item, label: [item.name, item.uniqueId].filter(Boolean).join(' | ') }));

      return Promise.resolve(list);
    },

    notFoundNode: () => {
      return (
        <V2Empty
          customTip={<div className={cs(styles.createEntrance, 'fs-12')}>
            <div style={{ color: '#cccccc' }}>暂无数据，</div>
            <div style={{ color: '#006AFF', cursor: 'pointer' }} onClick={methods.createHandle}>{createBtnTitle}</div>
            <IconFont iconHref='iconic_next_black_seven' className='fs-10 c-006' />
          </div>}
        />
      );
    },

    /**
     * @description 创建
     */
    createHandle: async() => {
      // 多条模板时需要选择，只有一条时直接使用
      const result: any = await getFranchiseeTemplateList();
      if (isArray(result) && result.length === 1) {
        setFormDrawerData({
          open: true,
          templateId: result[0].id,
          id: '',
        });
      } else if (isArray(result) && result.length > 1) {
        setSelectTemplateData({
          open: true,
          options: refactorSelection(result, { name: 'templateName' }),
        });
      }
    },

    onCreated(val) {
      onCreated?.(val);
      if (val) {
        getFranchiseeDetail({ id: val }).then(res => {
          if (res) {
            const { name, uniqueId } = res || {};
            res && fuzzyRef.current?.addOption?.({
              'id': val,
              'name': name,
              'uniqueId': uniqueId,
              'label': [name, uniqueId].filter(Boolean).join(' | '),
            });
          }
        });
      }
    },

  });

  return (
    <>
      <Form.Item
        name={name}
        label={label}
        {...formItemConfig}
      >
        <V2Fuzzy
          ref={fuzzyRef}
          loadData={methods.loadData}
          fieldNames={{
            label: 'label',
            value: 'id',
          }}
          notFoundNode={methods.notFoundNode()}
          {...props}
        />
      </Form.Item>
      {/* 模版选择框  */}
      <FranchiseeSelectTemplate
        templateData={selectTemplateData}
        setTemplateData={setSelectTemplateData}
        setFormDrawerData={setFormDrawerData}
      />
      <FranchiseeCreateDrawer
        drawerData={formDrawerData}
        setDrawerData={setFormDrawerData}
        onCreated={methods.onCreated}
      />
    </>
  );
});

export default FormFranchisee;
